import { isResource, resolveId } from '@medplum/core'
import type {
  Attachment,
  DiagnosticReport,
  Observation,
  Patient,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { chunk, uniq } from 'lodash-es'
import type { SeenMedplumClient } from '#src/client.ts'
import { IDENTIFIER_TYPE, IdentifierType } from '#src/codeSystems.ts'
import { HEALTH_GORILLA_IDENTIFIER_SYSTEM } from '#src/constants.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { parseQuestAccession } from '#src/models/document-references/labCopies.ts'
import { ExtendedObservation } from '#src/models/observation.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class ExtendedDiagnosticReport extends ExtendedResource<DiagnosticReport> {
  static readonly RESOURCE_TYPE = 'DiagnosticReport'

  static isInstance(value: unknown): value is DiagnosticReport {
    return isResource(value) && isOfType(value, 'DiagnosticReport')
  }

  static async findForResultDocument(
    medplum: Pick<
      SeenMedplumClient,
      'searchResourceClass' | 'searchResourceClassPagesAll'
    >,
    documentReferenceId: string
  ): Promise<ExtendedDiagnosticReport | undefined> {
    const leaves = await medplum.searchResourceClassPagesAll(
      ExtendedObservation,
      {
        'derived-from': `DocumentReference/${documentReferenceId}`,
        _sort: '_id',
        _count: '100',
      }
    )
    if (leaves.length === 0) {
      return undefined
    }
    const references = (observations: ExtendedObservation[]) =>
      observations.flatMap((observation) =>
        observation.id ? [`Observation/${observation.id}`] : []
      )
    const panels: ExtendedObservation[] = []
    for (const members of chunk(references(leaves), 100)) {
      panels.push(
        ...(await medplum.searchResourceClassPagesAll(ExtendedObservation, {
          'has-member': members,
          _sort: '_id',
          _count: '100',
        }))
      )
    }
    const reports: ExtendedDiagnosticReport[] = []
    for (const result of chunk(
      uniq(references(panels.length > 0 ? panels : leaves)),
      100
    )) {
      reports.push(
        ...(await medplum.searchResourceClass(ExtendedDiagnosticReport, {
          result,
          'status:not': 'entered-in-error',
          _sort: '-issued',
          _count: '1',
        }))
      )
    }
    // Prefer the most recently issued report when a result observation is shared.
    const issuedAt = (report: ExtendedDiagnosticReport) =>
      report.issued ? Date.parse(report.issued) : Number.NEGATIVE_INFINITY
    return [...reports].sort((a, b) => issuedAt(b) - issuedAt(a))[0]
  }

  // Bulk read: one search per chunk of ids. Health Gorilla ids are the untyped
  // identifier its bot writes on the report and on the documents it delivers.
  static byHealthGorillaIds(healthGorillaIds: string[]): QueryTypes {
    return {
      identifier: healthGorillaIds.map(
        (id) => `${HEALTH_GORILLA_IDENTIFIER_SYSTEM}|${id}`
      ),
      'status:not': 'entered-in-error',
      _count: 100,
    }
  }

  static byParticipantAndAccession(
    participant: Reference<Patient>,
    accession: string
  ): QueryTypes {
    return {
      subject: participant,
      identifier: accession,
      'status:not': 'entered-in-error',
      _count: 100,
    }
  }

  static byBasedOn(basedOn: Reference<ServiceRequest>) {
    return {
      'based-on': basedOn.reference,
    }
  }

  get status(): DiagnosticReport['status'] {
    return this.resource.status
  }

  get subject(): Reference<Patient> | undefined {
    return this.resource.subject as Reference<Patient> | undefined
  }

  get healthGorillaId(): string | undefined {
    return this.identifier(HEALTH_GORILLA_IDENTIFIER_SYSTEM)?.value
  }

  // Quest's report-level accession is the ACSN-typed identifier. The order-level
  // Lab Reference ID travels under the same type on other resources, so a value
  // that is not accession-shaped is rejected rather than trusted.
  get questAccession(): string | undefined {
    for (const id of this.identifiers()) {
      const isAccession = id.type?.coding?.some(
        (coding) =>
          coding.system === IDENTIFIER_TYPE &&
          coding.code === IdentifierType.Accession
      )
      const accession = isAccession ? parseQuestAccession(id.value) : undefined
      if (accession) {
        return accession
      }
    }
    return undefined
  }

  get code(): DiagnosticReport['code'] {
    return this.resource.code
  }

  get display(): string | undefined {
    return this.resource.code?.text
  }

  get issued(): string | undefined {
    return this.resource.issued
  }

  get effectiveDateTime(): string | undefined {
    return this.resource.effectiveDateTime
  }

  get effectivePeriod(): DiagnosticReport['effectivePeriod'] {
    return this.resource.effectivePeriod
  }

  get clinicalDate(): string | undefined {
    return this.effectiveDateTime ?? this.effectivePeriod?.start ?? this.issued
  }

  get result(): Reference<Observation>[] {
    return this.resource.result ?? []
  }

  get resultIds(): string[] {
    return this.result.map(resolveId).filter((id): id is string => !!id)
  }

  get presentedForm(): Attachment[] {
    return this.resource.presentedForm ?? []
  }

  get conclusion(): string | undefined {
    return this.resource.conclusion
  }

  get conclusionCode(): DiagnosticReport['conclusionCode'] {
    return this.resource.conclusionCode
  }

  get basedOn(): Reference<ServiceRequest>[] {
    return (
      this.resource.basedOn?.filter((b) => isOfType(b, 'ServiceRequest')) ?? []
    )
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      display: this.display,
      issued: this.issued,
      effectiveDateTime: this.effectiveDateTime,
      resultIds: this.resultIds,
      conclusion: this.conclusion,
    }
  }
}
